import { useEffect, useId, useRef } from 'react';
import './jqueryGlobals';
import $ from 'jquery';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import select2Factory from 'select2';
(select2Factory as unknown as (w: Window, jq: typeof $) => void)(window, $);

export interface Select2AsyncItem {
  value: string;
  text: string;
}

export interface Select2Page {
  items: Select2AsyncItem[];
  hasMore: boolean;
}

interface Select2AsyncProps {
  label?: string;
  placeholder?: string;
  className?: string;
  minInputLength?: number;
  // Paginated remote loader: returns a page of items + whether more pages exist,
  // so the dropdown loads options page-by-page on scroll (never the whole list).
  fetch: (term: string, page: number) => Promise<Select2Page>;
  // --- search-and-pick mode (e.g. navbar login search) ---
  onSelect?: (value: string, text: string) => void;
  clearOnSelect?: boolean;
  // --- controlled filter mode (value + onChange) ---
  value?: string | null;
  valueLabel?: string; // display text for the current value (defaults to value)
  onChange?: (value: string | null) => void;
  disabled?: boolean;
}

export function Select2Async({
  label,
  placeholder = 'Search…',
  className,
  minInputLength = 0,
  fetch,
  onSelect,
  clearOnSelect = false,
  value,
  valueLabel,
  onChange,
  disabled = false,
}: Select2AsyncProps) {
  const id = useId();
  const selectRef = useRef<HTMLSelectElement>(null);
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const controlled = onChange !== undefined;

  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;
    const $el = $(el);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ($el as any).select2({
      width: '100%',
      placeholder,
      allowClear: true,
      minimumInputLength: minInputLength,
      ajax: {
        delay: 250,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transport: (params: any, success: any, failure: any) => {
          const term = params?.data?.term ?? '';
          const page = params?.data?.page ?? 1;
          fetchRef.current(term, page)
            .then((res) =>
              success({
                results: res.items.map((i) => ({ id: i.value, text: i.text })),
                pagination: { more: res.hasMore },
              }),
            )
            .catch(() => failure && failure());
          return { abort: () => {} };
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        processResults: (data: any) => data,
      },
    });

    // Pre-seed the current value (controlled mode) so it displays without a fetch.
    if (controlled && value) {
      const opt = new Option(valueLabel ?? value, value, true, true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ($el as any).append(opt).trigger('change.select2');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSel = (e: any) => {
      const d = e.params?.data;
      if (!d) return;
      onSelectRef.current?.(String(d.id), String(d.text));
      onChangeRef.current?.(String(d.id));
      if (clearOnSelect) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ($el as any).val(null).trigger('change.select2');
      }
    };
    const onClear = () => onChangeRef.current?.(null);
    $el.on('select2:select', onSel);
    $el.on('select2:clear', onClear);

    return () => {
      $el.off('select2:select', onSel);
      $el.off('select2:clear', onClear);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (($el as any).data('select2')) ($el as any).select2('destroy');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder, minInputLength, clearOnSelect, controlled]);

  // Reflect the disabled state onto the underlying select (select2 mirrors it).
  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;
    el.disabled = disabled;
    const $el = $(el);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (($el as any).data('select2')) $el.trigger('change.select2');
  }, [disabled]);

  // Keep controlled value in sync (e.g. external Clear All Filters).
  useEffect(() => {
    if (!controlled) return;
    const el = selectRef.current;
    if (!el) return;
    const $el = $(el);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!($el as any).data('select2')) return;
    const current = ($el.val() as string | null) ?? '';
    if ((value ?? '') === current) return;
    if (!value) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ($el as any).val(null).trigger('change.select2');
    } else {
      if (!el.querySelector(`option[value="${CSS.escape(value)}"]`)) {
        el.append(new Option(valueLabel ?? value, value, true, true));
      }
      $el.val(value).trigger('change.select2');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className={className || undefined}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      )}
      <select id={id} ref={selectRef} className="form-control">
        <option value="" />
      </select>
    </div>
  );
}
